import { useEffect, lazy, Suspense, memo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CustomizationProvider } from "./contexts/CustomizationContext";
import ErrorBoundary from "./components/ErrorBoundary";
import OfflineBanner from "./components/OfflineBanner";
import PWAInstallPrompt from "./components/PWAInstallPrompt"; // disabled
import FaoAutoTrigger from "./components/FaoAutoTrigger";
import { useServiceWorkerUpdate } from "./hooks/useServiceWorkerUpdate";
import { useAppVersionCheck } from "./hooks/useAppVersionCheck";

// Intercepteur axios global pour gérer les erreurs de connexion DB
let isReconnecting = false;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 503 && !isReconnecting) {
      isReconnecting = true;
      toast.info('Reconnexion à la base de données...', { duration: 3000 });
      
      // Attendre 2 secondes puis rafraîchir
      setTimeout(() => {
        isReconnecting = false;
        window.location.reload();
      }, 2000);
    }
    return Promise.reject(error);
  }
);

// Lazy load heavy components for better initial load time.
// `webpackPrefetch: true` ajoute un <link rel="prefetch"> au HTML : le browser
// télécharge ces chunks en arrière-plan (priorité idle) après le first paint.
// Résultat : navigation quasi-instantanée vers ces pages (gain perçu ~300ms).
const Login = lazy(() => import("./pages/Login"));
const EmployeeDashboard = lazy(() => import(/* webpackPrefetch: true */ "./pages/EmployeeDashboardSimplified"));
const EmployeeHistory = lazy(() => import(/* webpackPrefetch: true */ "./pages/EmployeeHistory"));
const EmployeeAbsences = lazy(() => import(/* webpackPrefetch: true */ "./pages/EmployeeAbsences"));
const HRDashboard = lazy(() => import(/* webpackPrefetch: true */ "./pages/HRDashboard"));
const RegulationsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/RegulationsPage"));
const NotesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/NotesPage"));
const CustomizationPage = lazy(() => import("./pages/CustomizationPage"));
const MapPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/MapPage"));
const ForecastPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ForecastPage"));
const TestSessionUpload = lazy(() => import("./pages/TestSessionUpload"));
const NotFoundPage = lazy(() => import("./pages/ErrorPages").then(m => ({ default: m.NotFoundPage })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading spinner component - memoized to prevent re-renders - Ocre/Rouge theme
const LoadingSpinner = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ocre-50 to-ocre-100 dark:from-slate-900 dark:to-slate-800">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-ocre-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-theme-primary dark:text-theme-muted">Chargement...</p>
    </div>
  </div>
));

// AUTHENTIFICATION DÉSACTIVÉE - Accès direct sans login
// Tous les utilisateurs accèdent directement à l'interface
const ProtectedRoute = memo(({ children }) => {
  return children;
});

// Espace RH : réservé au rôle « hr ». Sans ce garde, une session employé
// (ex. restaurée par la PWA) pouvait afficher l'espace RH avec toutes les
// API en 403 → aucune donnée de pointage visible (bug signalé).
const HRProtectedRoute = memo(({ children }) => {
  const { user, token, loading, logout } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!token || !user) return <Navigate to="/" replace />;
  if (user.role !== 'hr') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="hr-access-denied">
        <div className="max-w-md space-y-4">
          <h1 className="text-xl font-bold">Espace RH — accès administrateur requis</h1>
          <p className="text-sm text-gray-600">
            Vous êtes connecté en tant que « {user.first_name || user.name} » (session employé).
            Pour consulter les pointages et les données RH, reconnectez-vous avec le compte Admin.
          </p>
          <button
            type="button"
            data-testid="hr-access-denied-relogin-btn"
            className="px-4 py-2 rounded-md bg-black text-white font-semibold"
            onClick={async () => { await logout(); window.location.href = '/'; }}
          >
            Se reconnecter en Admin
          </button>
        </div>
      </div>
    );
  }
  return children;
});

// Public Route Component - redirige vers le dashboard directement
const PublicRoute = memo(({ children }) => {
  // On affiche la page de choix simple au lieu du login
  return children;
});

// Seed data on first load and register service worker
const SeedData = memo(() => {
  useEffect(() => {
    const init = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Ignore if already seeded
      }
    };
    init();
  }, []);

  return null;
});

function AppRoutes() {
  // Toast non-bloquant "Nouvelle version disponible · Recharger" quand un
  // nouveau SW est en waiting. Évite à l'utilisateur d'aller manuellement
  // sur /reset-cache.html après chaque déploiement.
  useServiceWorkerUpdate();
  // Filet de sécurité anti-« PWA figée » : compare le BUILD_ID du bundle avec
  // /version.json et force purge SW + reload si l'appareil sert un vieux code.
  useAppVersionCheck();
  return (
    <>
      <SeedData />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route 
            path="/" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <EmployeeDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/history" 
            element={
              <ProtectedRoute>
                <EmployeeHistory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/absences" 
            element={
              <ProtectedRoute>
                <EmployeeAbsences />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/regulations" 
            element={
              <ProtectedRoute>
                <RegulationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notes" 
            element={
              <ProtectedRoute>
                <NotesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hr" 
            element={
              <HRProtectedRoute>
                <HRDashboard />
              </HRProtectedRoute>
            } 
          />
          <Route 
            path="/forecast" 
            element={
              <ProtectedRoute>
                <ForecastPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/customization" 
            element={
              <ProtectedRoute>
                <CustomizationPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/map" 
            element={
              <ProtectedRoute>
                <MapPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/test-session" 
            element={
              <ProtectedRoute>
                <TestSessionUpload />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <ThemeProvider>
            <CustomizationProvider>
              <AuthProvider>
                <OfflineBanner />
                <AppRoutes />
                <FaoAutoTrigger />
                <Toaster position="top-center" richColors />
              </AuthProvider>
            </CustomizationProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
